import { useState, useEffect, useRef } from "react";
import { useDataStore } from "@store/user_org";
import { organizationsApi } from "@services/api/organizations";
import { ordresApi } from "@services/api/ordres";
import { serviceboxProxy, type LogEntry, type TestConnectionResult, type HealthResult } from "@services/serviceboxProxy";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import TextField from "@components/ui/textfields/TextField";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import type { Ordre } from "types/ordre";
import type { OrgServiceBoxSite } from "types/org";

export function ServiceBoxSettings() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const sb = orgData?.servicebox;

  const [enabled, setEnabled] = useState(sb?.enabled || false);
  const [username, setUsername] = useState(sb?.username || "");
  const [password, setPassword] = useState(sb?.password || "");
  const [siteCode, setSiteCode] = useState(sb?.site_code || "");
  const [proxyUrl, setProxyUrl] = useState(sb?.proxy_url || "");
  const [sites, setSites] = useState<OrgServiceBoxSite[]>(sb?.sites || []);
  const [saving, setSaving] = useState(false);

  // Proxy status
  const [proxyHealth, setProxyHealth] = useState<HealthResult | null>(null);
  const [proxyChecking, setProxyChecking] = useState(false);

  // Connection test
  const [connectionTest, setConnectionTest] = useState<TestConnectionResult | null>(null);
  const [testing, setTesting] = useState(false);

  // Logs
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  // Transfer history
  const [transfers, setTransfers] = useState<Ordre[]>([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [deletingRdv, setDeletingRdv] = useState<string | null>(null);

  // Sync local state when org data changes
  useEffect(() => {
    if (sb) {
      setEnabled(sb.enabled || false);
      setUsername(sb.username || "");
      setPassword(sb.password || "");
      setSiteCode(sb.site_code || "");
      setProxyUrl(sb.proxy_url || "");
      setSites(sb.sites || []);
    }
  }, [sb?.enabled, sb?.username, sb?.password, sb?.site_code, sb?.proxy_url, sb?.sites]);

  // Check proxy health on mount + periodically
  useEffect(() => {
    checkProxy();
    const interval = setInterval(checkProxy, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch transfer history on mount
  useEffect(() => {
    if (orgData?.id) fetchTransfers();
  }, [orgData?.id]);

  const fetchTransfers = async () => {
    if (!orgData?.id) return;
    setTransfersLoading(true);
    try {
      const data = await ordresApi.list({
        org_id: orgData.id,
        has_or_number: true,
        limit: 50,
        sort_by: "created_at",
        sort_dir: "desc",
      });
      setTransfers(data.items);
    } catch {
      setTransfers([]);
    } finally {
      setTransfersLoading(false);
    }
  };

  const handleDeleteRdv = async (ordre: Ordre) => {
    if (!ordre.servicebox_rdv_id) {
      showNotification({ type: "error", message: "Pas de rdv_id ServiceBox stocke pour cet ordre" });
      return;
    }
    if (!sb?.username) return;
    if (!window.confirm(`Supprimer le RDV ServiceBox pour ${ordre.num} ?`)) return;

    setDeletingRdv(ordre.id);
    try {
      const result = await serviceboxProxy.deleteRdv(
        { username: sb.username, password: sb.password || "", site_code: sb.site_code },
        ordre.servicebox_rdv_id,
      );
      if (result.success) {
        // Clear ServiceBox fields on the ordre
        await ordresApi.update(ordre.id, {
          or_number: "",
          servicebox_rdv_id: "",
        } as any);
        showNotification({ type: "success", message: `RDV supprime de ServiceBox` });
        fetchTransfers();
      } else {
        showNotification({ type: "error", message: result.error || "Echec suppression" });
      }
    } catch (e: any) {
      showNotification({ type: "error", message: e.message });
    } finally {
      setDeletingRdv(null);
    }
  };

  const checkProxy = async () => {
    setProxyChecking(true);
    const result = await serviceboxProxy.checkHealth();
    setProxyHealth(result);
    setProxyChecking(false);
  };

  const handleSave = async () => {
    if (!orgData) return;
    setSaving(true);
    try {
      await organizationsApi.update(orgData.id, {
        servicebox: {
          enabled,
          username,
          password,
          site_code: siteCode,
          proxy_url: proxyUrl || undefined,
          sites: sites.length > 0 ? sites : undefined,
        },
      } as any);
      await fetchOrgData(orgData.id);
      showNotification({ type: "success", message: "Configuration ServiceBox sauvegardee" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionTest(null);
    try {
      const result = await serviceboxProxy.testConnection({
        username,
        password,
        site_code: siteCode,
      });
      setConnectionTest(result);
    } catch (e: any) {
      setConnectionTest({
        connected: false,
        session_ok: false,
        servicebox_reachable: false,
        detail: e.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleFetchLogs = async () => {
    setLogsLoading(true);
    try {
      const entries = await serviceboxProxy.getLogs(200);
      setLogs(entries);
      setShowLogs(true);
    } catch {
      setLogs([]);
      setShowLogs(true);
    } finally {
      setLogsLoading(false);
    }
  };

  const hasChanges =
    enabled !== (sb?.enabled || false) ||
    username !== (sb?.username || "") ||
    password !== (sb?.password || "") ||
    siteCode !== (sb?.site_code || "") ||
    proxyUrl !== (sb?.proxy_url || "") ||
    JSON.stringify(sites) !== JSON.stringify(sb?.sites || []);

  return (
    <div className="flex flex-col p-8 max-w-2xl">
      <h1 className="text-hx font-semibold mb-2" style={{ fontFamily: "Poppins" }}>
        ServiceBox
      </h1>
      <p className="text-h2 text-neutral-500 mb-8" style={{ fontFamily: "Poppins" }}>
        Configuration de l'integration avec ServiceBox (Stellantis). Le proxy
        local doit tourner sur le PC du garage pour permettre les transferts.
      </p>

      {/* Proxy status card */}
      <div
        className="flex flex-row items-center gap-4 p-4 rounded-xl mb-6"
        style={{
          background: proxyHealth ? "#F0FDF4" : "#FEF2F2",
          border: `1px solid ${proxyHealth ? "#BBF7D0" : "#FECACA"}`,
        }}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: proxyHealth ? "#DCFCE7" : "#FEE2E2" }}
        >
          <Icon1
            name={proxyHealth ? "done2" : "warning"}
            size={20}
            color={proxyHealth ? "#16A34A" : "#DC2626"}
          />
        </div>
        <div className="flex flex-col flex-1">
          <span
            className="text-h2 font-medium"
            style={{ color: proxyHealth ? "#166534" : "#991B1B" }}
          >
            {proxyHealth ? "Proxy connecte" : "Proxy hors ligne"}
          </span>
          <span className="text-h4 text-neutral-500">
            {proxyHealth
              ? `Version ${proxyHealth.version} — ${proxyHealth.sessions} session(s) active(s)`
              : `Le proxy ServiceBox n'est pas joignable sur ${sb?.proxy_url || "localhost:3847"}`}
          </span>
        </div>
        <SecondaryButton
          variant="small"
          label={proxyChecking ? "..." : "Verifier"}
          onClick={checkProxy}
          disabled={proxyChecking}
        />
      </div>

      {/* Proxy already installed — shown when proxy registered but not reachable locally, and seen in last 24h */}
      {!proxyHealth && sb?.proxy_last_seen && (Date.now() - new Date(sb.proxy_last_seen).getTime()) < 86400000 && (() => {
        const diffMin = (Date.now() - new Date(sb.proxy_last_seen).getTime()) / 60000;
        const isRecent = diffMin < 30;
        return (
          <div
            className="flex flex-col gap-3 p-5 rounded-xl mb-6"
            style={{ background: isRecent ? "#F0FDF4" : "#FFFBEB", border: `1px solid ${isRecent ? "#BBF7D0" : "#FDE68A"}` }}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isRecent ? "bg-green-500" : "bg-yellow-500"}`} />
              <span className="text-h1 font-semibold text-neutral-800">
                Proxy déjà installé
              </span>
            </div>
            <p className="text-h3 text-neutral-600">
              Un proxy est déjà enregistré pour cette organisation
              {sb.proxy_version && <span> (v{sb.proxy_version})</span>}.
              {isRecent
                ? " Il est actuellement en ligne et fonctionne normalement."
                : ` Dernier signal il y a ${Math.floor(diffMin)} minutes.`}
            </p>
            <p className="text-h4 text-neutral-400">
              Le proxy n'est pas joignable depuis ce navigateur car il est installé sur un autre poste du réseau.
              Si vous devez le réinstaller, contactez un administrateur.
            </p>
          </div>
        );
      })()}

      {/* Installation section — shown when no proxy registered, or proxy dead (no signal > 24h) */}
      {!proxyHealth && (!sb?.proxy_last_seen || (Date.now() - new Date(sb.proxy_last_seen).getTime()) >= 86400000) && (
        <div
          className="flex flex-col gap-4 p-5 rounded-xl mb-6"
          style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
        >
          <span className="text-h1 font-semibold text-neutral-800">Installation du proxy</span>
          <p className="text-h3 text-neutral-500">
            Le proxy doit être installé sur un PC connecté au VPN Stellantis.
          </p>
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-h4 font-bold shrink-0">1</span>
              <span className="text-h3 text-neutral-700">Téléchargez le fichier sur le PC du garage</span>
            </div>
            <button
              onClick={async () => {
                try {
                  const { default: api } = await import("@services/api/client");
                  const resp = await api.get("/admin/proxies/download", { responseType: "blob" });
                  const url = window.URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "servicebox-proxy.exe";
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch {
                  showNotification({ message: "Erreur lors du téléchargement", type: "error" });
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white text-h2 font-medium hover:bg-blue-600 w-fit ml-9 cursor-pointer"
            >
              <Icon1 name="download" size={18} color="#fff" />
              Télécharger servicebox-proxy.exe
            </button>
            <div className="flex flex-row items-center gap-3 mt-1">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-h4 font-bold shrink-0">2</span>
              <span className="text-h3 text-neutral-700">Double-cliquez dessus et acceptez l'installation</span>
            </div>
            <span className="text-h4 text-neutral-400 ml-9">
              L'installation est automatique. Le proxy détecte votre réseau et s'enregistre tout seul.
            </span>
          </div>
        </div>
      )}

      {/* Enable toggle */}
      <div className="mb-6">
        <SwitchBrick
          checked={enabled}
          onChange={(v) => setEnabled(v)}
          title="Activer ServiceBox"
          description="Affiche les options de transfert dans les ordres"
        />
      </div>

      {/* Credentials */}
      <div
        className="flex flex-col gap-4 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <span className="text-h1 font-semibold text-neutral-800">Identifiants</span>
        <TextField
          label="Nom d'utilisateur"
          value={username}
          onChange={setUsername}
          placeholder="Ex: DF09057"
        />
        <TextField
          label="Mot de passe"
          value={password}
          onChange={setPassword}
          type="password"
        />
        <TextField
          label="Code site (PDV)"
          value={siteCode}
          onChange={setSiteCode}
          placeholder="Ex: 011622H"
        />
        <TextField
          label="URL du proxy"
          value={proxyUrl}
          onChange={setProxyUrl}
          placeholder="http://localhost:3847"
        />
        <span className="text-h4 text-neutral-400 -mt-2">
          Adresse du PC qui fait tourner le proxy. Laisser vide pour localhost.
        </span>
        <div className="flex flex-row items-center gap-2 pt-2">
          <Button
            label={saving ? "Sauvegarde..." : "Sauvegarder"}
            onClick={handleSave}
            disabled={saving || !hasChanges}
          />
          {hasChanges && (
            <span className="text-h4 text-orange-500">Modifications non sauvegardees</span>
          )}
        </div>
      </div>

      {/* Sites / Marques */}
      <div
        className="flex flex-col gap-4 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <span className="text-h1 font-semibold text-neutral-800">Points de vente (marques)</span>
        <span className="text-h4 text-neutral-400 -mb-2">
          Associez chaque marque a son code PDV ServiceBox. La marque du vehicule sera utilisee pour
          selectionner automatiquement le bon point de vente lors du transfert.
        </span>

        {sites.map((site, idx) => (
          <div key={idx} className="flex flex-row items-end gap-2">
            <div className="w-[120px] shrink-0">
              <TextField
                label="Marque"
                value={site.marque}
                onChange={(v) => {
                  const copy = [...sites];
                  copy[idx] = { ...copy[idx], marque: v };
                  setSites(copy);
                }}
                placeholder="Ex: PEUGEOT"
              />
            </div>
            <div className="w-[120px] shrink-0">
              <TextField
                label="Code PDV"
                value={site.pdv_id}
                onChange={(v) => {
                  const copy = [...sites];
                  copy[idx] = { ...copy[idx], pdv_id: v };
                  setSites(copy);
                }}
                placeholder="Ex: 448174E"
              />
            </div>
            <div className="flex-1">
              <TextField
                label="Libellé"
                value={site.label}
                onChange={(v) => {
                  const copy = [...sites];
                copy[idx] = { ...copy[idx], label: v };
                setSites(copy);
              }}
              placeholder="Ex: PEUGEOT HERIC AUTOMOBILES"
            />
            </div>
            <button
              className="mb-1 p-2 text-red-400 hover:text-red-600"
              onClick={() => setSites(sites.filter((_, i) => i !== idx))}
            >
              <Icon1 name="close" size={16} />
            </button>
          </div>
        ))}

        <SecondaryButton
          variant="small"
          label="Ajouter un point de vente"
          onClick={() => setSites([...sites, { marque: "", pdv_id: "", label: "" }])}
        />
      </div>

      {/* Test connection */}
      <div
        className="flex flex-col gap-3 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <span className="text-h1 font-semibold text-neutral-800">Diagnostics</span>
        <div className="flex flex-row items-center gap-2">
          <SecondaryButton
            label={testing ? "Test en cours..." : "Tester la connexion"}
            onClick={handleTestConnection}
            disabled={testing || !proxyHealth || !username}
          />
          <SecondaryButton
            label={logsLoading ? "Chargement..." : showLogs ? "Masquer les logs" : "Voir les logs"}
            onClick={() => (showLogs ? setShowLogs(false) : handleFetchLogs())}
            disabled={logsLoading || !proxyHealth}
          />
        </div>

        {/* Connection test result */}
        {connectionTest && (
          <div
            className="p-3 rounded-lg border text-h3"
            style={{
              background: connectionTest.connected ? "#F0FDF4" : "#FEF2F2",
              borderColor: connectionTest.connected ? "#BBF7D0" : "#FECACA",
              color: connectionTest.connected ? "#166534" : "#991B1B",
            }}
          >
            <div className="font-medium mb-1">
              {connectionTest.connected ? "Connexion OK" : "Echec de connexion"}
            </div>
            <div className="text-h4">
              VPN/Reseau: {connectionTest.servicebox_reachable ? "OK" : "Echec"} | Session:{" "}
              {connectionTest.session_ok ? "OK" : "Echec"}
            </div>
            {connectionTest.detail && (
              <div className="text-h4 mt-1 opacity-80">{connectionTest.detail}</div>
            )}
          </div>
        )}

        {/* Logs panel */}
        {showLogs && (
          <div className="flex flex-col border border-neutral-200 rounded-lg overflow-hidden">
            <div className="flex flex-row items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-200">
              <span className="text-h3 font-medium text-neutral-600">
                Logs du proxy ({logs.length})
              </span>
              <div className="flex flex-row items-center gap-2">
                <button
                  onClick={() => {
                    const text = logs.map((l) => `${l.ts.split("T")[1]?.slice(0, 8)} [${l.step}] ${l.message}`).join("\n");
                    navigator.clipboard.writeText(text);
                    showNotification({ type: "success", message: "Logs copies" });
                  }}
                  className="text-h4 text-blue-500 hover:text-blue-700"
                >
                  Copier
                </button>
                <button
                  onClick={handleFetchLogs}
                  className="text-h4 text-blue-500 hover:text-blue-700"
                >
                  Rafraichir
                </button>
              </div>
            </div>
            <div className="max-h-[600px] overflow-y-auto p-2 bg-neutral-900 font-mono text-[11px] leading-[16px]">
              {logs.length === 0 ? (
                <div className="text-neutral-500 py-4 text-center">Aucun log</div>
              ) : (
                logs.map((log, i) => {
                  const color =
                    log.level === "error"
                      ? "#F87171"
                      : log.level === "warn"
                        ? "#FBBF24"
                        : "#9CA3AF";
                  return (
                    <div key={i} style={{ color }}>
                      <span className="text-neutral-600">
                        {log.ts.split("T")[1]?.slice(0, 8)}
                      </span>{" "}
                      {log.step && <span className="text-blue-400">[{log.step}]</span>}{" "}
                      {log.message}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Transfer history */}
      <div
        className="flex flex-col gap-3 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <div className="flex flex-row items-center justify-between">
          <span className="text-h1 font-semibold text-neutral-800">
            Historique des transferts
          </span>
          <SecondaryButton
            variant="small"
            label={transfersLoading ? "..." : "Rafraichir"}
            onClick={fetchTransfers}
            disabled={transfersLoading}
          />
        </div>

        {transfersLoading && transfers.length === 0 ? (
          <div className="text-h3 text-neutral-400 py-4 text-center">Chargement...</div>
        ) : transfers.length === 0 ? (
          <div className="text-h3 text-neutral-400 py-4 text-center">
            Aucun transfert ServiceBox effectue
          </div>
        ) : (
          <div className="flex flex-col border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-h3">
              <thead>
                <tr className="bg-neutral-100 text-neutral-500 text-left">
                  <th className="px-3 py-2 font-medium">OR</th>
                  <th className="px-3 py-2 font-medium">Ordre</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Vehicule</th>
                  <th className="px-3 py-2 font-medium">Date creation</th>
                  <th className="px-3 py-2 font-medium">Statut</th>
                  <th className="px-3 py-2 font-medium w-[80px]"></th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((o) => {
                  const contact = o.contacts?.find((c) => c.is_primary) || o.contacts?.[0];
                  const created = o.created_at?.seconds
                    ? new Date(o.created_at.seconds * 1000)
                    : null;
                  return (
                    <tr
                      key={o.id}
                      className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                      onClick={() => navigateTo({ route: `/ordre/${o.id}` })}
                    >
                      <td className="px-3 py-2 font-medium text-blue-600">
                        {o.or_number || "\u2014"}
                      </td>
                      <td className="px-3 py-2 text-neutral-700">{o.num}</td>
                      <td className="px-3 py-2 text-neutral-600">
                        {contact ? `${contact.prenom || ""} ${contact.nom || ""}`.trim() : o.name}
                      </td>
                      <td className="px-3 py-2 text-neutral-600">
                        {o.immatriculation || o.vehicle?.plate || "\u2014"}
                      </td>
                      <td className="px-3 py-2 text-neutral-500">
                        {created
                          ? created.toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "\u2014"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-h4 font-medium"
                          style={{
                            background: o.status === "restitue" ? "#DCFCE7" : "#E0F2FE",
                            color: o.status === "restitue" ? "#166534" : "#0369A1",
                          }}
                        >
                          {o.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {o.servicebox_rdv_id && proxyHealth && (
                          <button
                            className="text-h4 text-red-400 hover:text-red-600 underline disabled:opacity-50"
                            disabled={deletingRdv === o.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRdv(o);
                            }}
                          >
                            {deletingRdv === o.id ? "..." : "Supprimer"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
